import React, { useCallback, useEffect } from "react";
import classNames from "classnames";
import { NavBar as DSNav, MENUBAR_ID } from "widgets/NavBar";
import { LanguageSelector } from "./LanguageSelector";
import { NavBarHelpCTAs } from "./NavBarHelpCTAs";
import {
    PatientAppAuthContext,
    PatientAppChatContext,
    PatientAppHelpCenterContext,
    PatientAppLocaleContext,
    PatientAppMenuContext,
    PatientAppPaymentOptionsContext,
    PatientAppBillingMenuContext,
    PatientAppBannerContext,
    VerifyOrLogoutColor,
} from "./interfaces";
import { MyAccountMenuDesktop } from "./MyAccountMenuDesktop";
import { MyAccountMenuMobile } from "./MyAccountMenuMobile";
import { BillingMenu } from "./BillingMenu";
import { PaymentOptionsMenu } from "./PaymentOptionsMenu";
import { NAV_MENU_V3_ENGAGEMENTS } from "./constants";
import { recordPatientAppClickEngagement } from "../../utils/engagements";
import { isLinkExternal } from "../../utils/url";
import {
    emptyBillingMenuContext,
    emptyMenuContext,
    emptyPaymentOptionsContext,
} from "widgets/src/NavBar/EmptyNavBarContexts";
import { BREAKPOINTS, useResizeObserver } from "design_system/src/hooks/useResizeObserver";
import { useMemo } from "react";
import { storageFactory } from "../../utils/storageFactory";

export interface NavBarProps {
    patientAppAuthContext: PatientAppAuthContext;
    patientAppLocaleContext: PatientAppLocaleContext;
    patientAppHelpCenterContext: PatientAppHelpCenterContext;
    patientAppChatContext: PatientAppChatContext;
    patientAppMenuContext: PatientAppMenuContext;
    patientAppPaymentsOptionsContext: PatientAppPaymentOptionsContext;
    patientAppBillingMenuContext: PatientAppBillingMenuContext;
    patientAppBannerContext: PatientAppBannerContext;
    formattedPhoneCallInbound: string;
    providerBusinessUnitLogoSrc: string;
    providerName: string;
    isSuppressed: boolean;
}

export const NavBar = ({
    patientAppAuthContext,
    patientAppLocaleContext,
    patientAppHelpCenterContext,
    patientAppChatContext,
    patientAppMenuContext = emptyMenuContext,
    patientAppPaymentsOptionsContext = emptyPaymentOptionsContext,
    patientAppBillingMenuContext = emptyBillingMenuContext,
    patientAppBannerContext,
    formattedPhoneCallInbound,
    providerBusinessUnitLogoSrc,
    providerName,
    isSuppressed,
}: NavBarProps) => {
    const {
        notificationCenterUrl,
        providerLogoUrl,
        preVisitAllVisitsUrl,
        financialAssistanceUrl,
        insuranceConsentUrl,
        insuranceUrl,
        verifyOrLogout,
        notifyAboutInvalidOrMissingContactInfo,
    } = patientAppMenuContext;
    const [width] = useResizeObserver();
    const isNarrowScreen = width > 0 && width < BREAKPOINTS.s768;

    const hasDismissedBannerAboveNav = useMemo(() => {
        const bannerAboveNav = document.getElementsByClassName("_banner_above_nav")[0];
        const bannerContent = bannerAboveNav?.textContent?.trim();
        if (!bannerContent) {
            return;
        }
        const key = window.cedar.generateElementKeyByString(bannerContent);
        const sessionStore = storageFactory(() => sessionStorage);
        return key ? sessionStore.getItem(key) === "true" : false;
    }, []);

    const handleHideBannersOnOpen = useCallback(
        (open: boolean) => {
            const welcomeBannerElement = document.getElementsByClassName("welcome-banner")[0];
            if (welcomeBannerElement) {
                open
                    ? welcomeBannerElement.classList.add("hidden")
                    : welcomeBannerElement.classList.remove("hidden");
            }

            const bannerAboveNav = document.getElementsByClassName("_banner_above_nav")[0];
            if (bannerAboveNav) {
                open
                    ? (bannerAboveNav as HTMLElement).style.setProperty("display", "none")
                    : !hasDismissedBannerAboveNav &&
                      (bannerAboveNav as HTMLElement).style.setProperty("display", "block");
            }
        },
        [hasDismissedBannerAboveNav],
    );

    useEffect(() => {
        // Reset the welcome banner visibility when the screen is widened
        if (!isNarrowScreen) {
            handleHideBannersOnOpen(false);
        }
    }, [isNarrowScreen, handleHideBannersOnOpen]);

    const anonymousPaths = ["/", "/find-account/"];
    const isProviderViewing = patientAppBannerContext?.isProviderViewing;

    return (
        <DSNav.Container enableHideOnScroll={!isProviderViewing}>
            {!isSuppressed && (
                <DSNav.TopBar
                    className={classNames({ hidden: isProviderViewing })}
                    aria-label={gettext("Information menu")}
                    // Minimal scrolling is necessary to display the top bar when
                    // it was partially hidden and a button inside it is focused
                    onFocus={() => window.scrollBy({ top: -1 })}
                >
                    <LanguageSelector
                        patientAppAuthContext={patientAppAuthContext}
                        patientAppLocaleContext={patientAppLocaleContext}
                    />
                    <NavBarHelpCTAs
                        helpCenterContext={patientAppHelpCenterContext}
                        isChatEnabled={patientAppChatContext.enabled}
                        providerPhoneNumber={formattedPhoneCallInbound}
                    />
                    {notificationCenterUrl && (
                        <MyAccountMenuDesktop
                            insuranceConsentUrl={insuranceConsentUrl}
                            insuranceUrl={insuranceUrl}
                            verifyOrLogout={verifyOrLogout}
                            notificationCenterUrl={notificationCenterUrl}
                            notifyAboutInvalidOrMissingContactInfo={
                                notifyAboutInvalidOrMissingContactInfo
                            }
                        />
                    )}
                </DSNav.TopBar>
            )}
            <DSNav
                role="menubar"
                aria-label={gettext("Main navigation")}
                menubarId={MENUBAR_ID}
                logo={
                    <DSNav.Logo
                        aria-label={gettext("Home link")}
                        href={providerLogoUrl || undefined}
                    >
                        <img
                            alt={`${providerName} logo`}
                            src={providerBusinessUnitLogoSrc}
                            onClick={() =>
                                recordPatientAppClickEngagement(
                                    NAV_MENU_V3_ENGAGEMENTS.PROVIDER_LOGO_BTN,
                                )
                            }
                        />
                    </DSNav.Logo>
                }
                showNotificationDot={notifyAboutInvalidOrMissingContactInfo}
                menuButtonClassname={
                    anonymousPaths.includes(window?.location?.pathname) ? "hidden" : undefined
                }
                onMenuButtonClick={handleHideBannersOnOpen}
            >
                {patientAppBillingMenuContext.visible && (
                    <BillingMenu {...patientAppBillingMenuContext} />
                )}
                {preVisitAllVisitsUrl && (
                    <DSNav.Link
                        href={preVisitAllVisitsUrl}
                        onClick={() =>
                            recordPatientAppClickEngagement(
                                NAV_MENU_V3_ENGAGEMENTS.UPCOMING_VISITS_BTN,
                            )
                        }
                    >
                        {gettext("Upcoming visits")}
                    </DSNav.Link>
                )}
                {patientAppPaymentsOptionsContext.visible && (
                    <PaymentOptionsMenu {...patientAppPaymentsOptionsContext} />
                )}
                {financialAssistanceUrl && (
                    <DSNav.Link
                        href={financialAssistanceUrl}
                        aria-label={
                            isLinkExternal(financialAssistanceUrl)
                                ? gettext("Financial assistance (link is external)")
                                : gettext("Financial assistance")
                        }
                        onClick={() =>
                            recordPatientAppClickEngagement(
                                NAV_MENU_V3_ENGAGEMENTS.FINANCIAL_ASSISTANCE_BTN,
                            )
                        }
                    >
                        {gettext("Financial assistance")}
                    </DSNav.Link>
                )}
                {notificationCenterUrl && !isProviderViewing && (
                    <MyAccountMenuMobile
                        insuranceConsentUrl={insuranceConsentUrl}
                        insuranceUrl={insuranceUrl}
                        notificationCenterUrl={notificationCenterUrl}
                        notifyAboutInvalidOrMissingContactInfo={
                            notifyAboutInvalidOrMissingContactInfo
                        }
                    />
                )}
                {verifyOrLogout && (
                    <DSNav.Link
                        href={verifyOrLogout.url}
                        className="s768:hidden"
                        text={
                            verifyOrLogout.color === VerifyOrLogoutColor.PRIMARY
                                ? "text-primary-3667e9"
                                : "text-red-c34648"
                        }
                        onClick={() =>
                            recordPatientAppClickEngagement(verifyOrLogout.engagementTarget)
                        }
                    >
                        {verifyOrLogout.cta}
                    </DSNav.Link>
                )}
            </DSNav>
        </DSNav.Container>
    );
};
